import {
  canEdit,
  removeInstanceMember,
  setInstanceMemberRole,
  updateMyInstanceProfile,
} from '../store.js';
import { memberRoleLabel, renderMemberRoleSelect } from '../config/member-roles.js';
import { getSession, getUserId } from '../supabase-client.js';
import { getContext, refreshLiveData } from '../trip-context.js';
import { buildInviteUrl, listTripInvites, revokeTripInvite } from '../trip-invites.js';
import { bindAvatarEmojiPicker, readAvatarEmojiFromForm, renderAvatarEmojiPicker } from '../utils/avatar-emoji-picker.js';
import { escapeHtml } from '../utils/dom.js';
import { formatDateShortIT } from '../utils/format.js';
import { bindInviteForm, renderInviteFormHtml } from './invite-modal.js';

function isInviteOpen(invite) {
  return !invite.accepted_at && new Date(invite.expires_at) > new Date();
}

function renderSelfProfileSection(member) {
  if (!member) return '';

  return `
    <section class="trip-group__section trip-group__section--profile">
      <h3 class="trip-group__heading">Il tuo profilo</h3>
      <form class="trip-group__profile-form" id="trip-group-self-profile">
        <label class="field">
          <span class="field-label">Nome in app</span>
          <input class="field-input" name="displayName" required maxlength="40" value="${escapeHtml(member.display_name)}" />
        </label>
        <div class="field">
          <span class="field-label">Emoji</span>
          ${renderAvatarEmojiPicker({ name: 'avatarEmoji', selected: member.avatar_emoji ?? '👤' })}
        </div>
        <button type="submit" class="btn-ghost btn-ghost--sm">Salva profilo</button>
      </form>
    </section>
  `;
}

function renderMemberRow(member, { isOwner, canManage, currentUserId }) {
  const isSelf = member.user_id === currentUserId;
  const showRoleControl = canManage && !isOwner && !isSelf;
  const removeBtn =
    canManage && !isSelf && !isOwner
      ? `<button type="button" class="trip-group__remove btn-ghost btn-ghost--sm" data-remove-member="${escapeHtml(member.user_id)}" aria-label="Rimuovi ${escapeHtml(member.display_name)}">Rimuovi</button>`
      : '';

  const roleBadge = isOwner
    ? '<span class="trip-group__role">organizzatore</span>'
    : showRoleControl
      ? ''
      : `<span class="trip-group__role">${escapeHtml(memberRoleLabel(member.role ?? 'editor'))}</span>`;

  const roleControl = showRoleControl
      ? `
        <label class="trip-group__role-field">
          <span class="sr-only">Ruolo per ${escapeHtml(member.display_name)}</span>
          ${renderMemberRoleSelect(`member-role-${member.user_id}`, member.role ?? 'editor', { memberUserId: member.user_id, compact: true })}
        </label>
      `
      : '';

  return `
    <li class="trip-group__row">
      <span class="trip-group__person">
        <span class="trip-group__emoji" aria-hidden="true">${member.avatar_emoji ?? '👤'}</span>
        <span class="trip-group__name">${escapeHtml(member.display_name)} ${roleBadge}</span>
      </span>
      <div class="trip-group__member-actions">
        ${roleControl}
        ${removeBtn}
      </div>
    </li>
  `;
}

function renderInviteRow(invite) {
  const open = isInviteOpen(invite);
  const status = open
    ? `In attesa · scade ${formatDateShortIT(invite.expires_at.slice(0, 10))}`
    : 'Scaduto';

  return `
    <li class="trip-group__row trip-group__row--invite">
      <div class="trip-group__person">
        <span class="trip-group__emoji" aria-hidden="true">${invite.avatar_emoji ?? '✉️'}</span>
        <div class="trip-group__invite-meta">
          <span class="trip-group__name">${escapeHtml(invite.display_name)}</span>
          <span class="trip-group__sub">${escapeHtml(invite.email)} · ${escapeHtml(memberRoleLabel(invite.role ?? 'editor'))} · ${status}</span>
        </div>
      </div>
      <div class="trip-group__invite-actions">
        ${
          open
            ? `<button type="button" class="btn-ghost btn-ghost--sm" data-copy-invite="${escapeHtml(invite.token)}">Copia link</button>`
            : ''
        }
        <button type="button" class="btn-ghost btn-ghost--sm trip-group__remove" data-revoke-invite="${escapeHtml(invite.id)}">Revoca</button>
      </div>
    </li>
  `;
}

/**
 * @param {object} opts
 * @param {Array} opts.members
 * @param {Array} opts.invites
 * @param {string} opts.ownerId
 * @param {string|null} opts.currentUserId
 * @param {boolean} opts.canManage
 * @param {object|null} opts.selfMember
 */
export function renderTripGroupPanel({
  members = [],
  invites = [],
  ownerId,
  currentUserId,
  canManage,
  selfMember = null,
}) {
  const openInvites = invites.filter((i) => !i.accepted_at);
  const memberRows = members
    .map((m) =>
      renderMemberRow(m, {
        isOwner: m.user_id === ownerId,
        canManage,
        currentUserId,
      }),
    )
    .join('');

  const inviteRows = openInvites.map(renderInviteRow).join('');

  return `
    <div class="trip-group" id="trip-group-panel">
      ${renderSelfProfileSection(selfMember)}
      <section class="trip-group__section">
        <h3 class="trip-group__heading">Partecipanti</h3>
        ${
          members.length
            ? `<ul class="trip-group__list">${memberRows}</ul>`
            : '<p class="trip-group__empty">Nessun partecipante</p>'
        }
      </section>

      ${
        openInvites.length
          ? `
        <section class="trip-group__section">
          <h3 class="trip-group__heading">Inviti</h3>
          <ul class="trip-group__list">${inviteRows}</ul>
        </section>
      `
          : ''
      }

      ${
        canManage
          ? `
        <button type="button" class="custom-add-trigger trip-group__invite-btn" data-open-invite-inline>
          <span class="custom-add-trigger__icon" aria-hidden="true">+</span>
          <span class="custom-add-trigger__label">Invita al viaggio</span>
        </button>
      `
          : ''
      }
    </div>
  `;
}

export async function loadTripGroupData(tripId) {
  return listTripInvites(tripId);
}

export function bindTripGroupPanel(container, shellCtx, { onMembersChange } = {}) {
  const panel = container.querySelector('#trip-group-panel');
  if (!panel) return;

  bindAvatarEmojiPicker(panel);

  panel.querySelector('#trip-group-self-profile')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await updateMyInstanceProfile(shellCtx.instanceId, {
        displayName: String(fd.get('displayName')),
        avatarEmoji: readAvatarEmojiFromForm(e.target),
      });
      await refreshLiveData({ forceNetwork: true });
      onMembersChange?.();
    } catch (error) {
      alert(error.message);
    }
  });

  panel.querySelectorAll('[data-member-role]').forEach((select) => {
    select.addEventListener('change', async () => {
      try {
        await setInstanceMemberRole(shellCtx.instanceId, select.dataset.memberRole, select.value);
        await refreshLiveData({ forceNetwork: true });
        onMembersChange?.();
      } catch (error) {
        alert(error.message);
        onMembersChange?.();
      }
    });
  });

  panel.querySelector('[data-open-invite-inline]')?.addEventListener('click', () => {
    if (!canEdit()) return;
    const slot = container.querySelector('#trip-group-slot');
    if (!slot) return;
    slot.innerHTML = `
      <div class="trip-group trip-group--invite" id="trip-group-invite-inline">
        <button type="button" class="btn-ghost btn-ghost--sm trip-group__back" data-invite-back>← Gruppo</button>
        <h3 class="trip-group__heading">Invita al viaggio</h3>
        ${renderInviteFormHtml({ tripTitle: shellCtx.tripTitle ?? 'viaggio', formId: 'trip-group-invite-form' })}
      </div>
    `;
    const inviteRoot = slot.querySelector('#trip-group-invite-inline');
    bindInviteForm(inviteRoot, {
      tripId: shellCtx.tripId,
      instanceId: shellCtx.instanceId,
      formId: 'trip-group-invite-form',
      onCreated: () => onMembersChange?.(),
      onCancel: () => refreshTripGroupPanel(container, shellCtx),
    });
    inviteRoot.querySelector('[data-invite-back]')?.addEventListener('click', () => {
      refreshTripGroupPanel(container, shellCtx);
    });
  });

  panel.querySelectorAll('[data-copy-invite]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const url = buildInviteUrl(btn.dataset.copyInvite);
      try {
        await navigator.clipboard.writeText(url);
        btn.textContent = 'Copiato!';
        setTimeout(() => {
          btn.textContent = 'Copia link';
        }, 2000);
      } catch {
        window.prompt('Copia il link invito:', url);
      }
    });
  });

  panel.querySelectorAll('[data-revoke-invite]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Revocare questo invito? Il link non funzionerà più.')) return;
      try {
        await revokeTripInvite(btn.dataset.revokeInvite);
        onMembersChange?.();
      } catch (error) {
        alert(error.message);
      }
    });
  });

  panel.querySelectorAll('[data-remove-member]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Rimuovere questa persona dal viaggio?')) return;
      try {
        await removeInstanceMember(shellCtx.instanceId, btn.dataset.removeMember);
        await refreshLiveData({ forceNetwork: true });
        onMembersChange?.();
      } catch (error) {
        alert(error.message);
      }
    });
  });
}

export async function refreshTripGroupPanel(container, shellCtx) {
  const ctx = getContext();
  if (!ctx?.instance?.id) return;

  const session = await getSession();
  const currentUserId = getUserId(session);
  const invites = await loadTripGroupData(shellCtx.tripId);
  const slot = container.querySelector('#trip-group-slot');
  if (!slot) return;

  const selfMember = ctx.live.members.find((m) => m.user_id === currentUserId) ?? null;
  const isOwner = currentUserId === ctx.instance.owner_id;

  slot.innerHTML = renderTripGroupPanel({
    members: ctx.live.members,
    invites,
    ownerId: ctx.instance.owner_id,
    currentUserId,
    canManage: isOwner,
    selfMember,
  });

  bindTripGroupPanel(container, shellCtx, {
    onMembersChange: () => refreshTripGroupPanel(container, shellCtx),
  });
}
